import React, {useCallback, useEffect} from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import { addProject, getProjectTabs } from '../../../../features/projects';
import { filterProjects } from '../../../../features/projects/filter';
import { getAllEmployees } from '../../../../features/employees/short';
import { getAllClients } from '../../../../features/clients/filter';
import { getRoles } from '../../../../features/roles/filter';
import { PIdNameModel, Project } from '../../../../features/projects/types';
import { REmployees } from '../../../../features/roles/types';
import BaseForm from '../../../../components/shared/form-controls/base-form';
import { FormInput } from '../../../../components/shared/form-controls/form-input';
import { FormCheckbox } from '../../../../components/shared/form-controls/form-checkbox';
import { DropdownField } from '../../../../components/shared/nextgen/dropdown-field';
import FormAutocomplete from '../../../../components/shared/form-controls/form-autocomplete';
import { Lx } from '../../../../i18n/consts';
import { NAVIGATION_ROUTES } from '../../../../routes/NavigationRoute';
import {
    canManageProjectClient,
    canManageProjectEmployees,
    canManageProjectManager,
    canManageProjectRoleForNotification,
    canManageProjectType,
    canManageProjects,
} from '../../../../features/profile/entityPermissions';
import { PROJECT_NAME_MAX_LENGTH } from '../../constants';

interface CreateForm {
    name: string;
    isActive: boolean;
    isBillable: boolean;
    projectType: PIdNameModel;
    client?: PIdNameModel;
    projectManager?: PIdNameModel;
    roleForNotification?: PIdNameModel;
    employees?: REmployees[];
    note?: string;
    isDefault?: boolean;
}

export default function CreateProjectForm() {
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const navigate = useNavigate();
    const params = useParams<{ typeId?: string }>();
    const profile = useAppSelector(store => store.profile);
    const types = useAppSelector(store => store.projects.types);
    const employees = useAppSelector(store => store.rEmployees);
    const clients = useAppSelector(store => store.pClients);
    const roles = useAppSelector(store => store.eRoles);
    const [isLoadingEmployees, setIsLoadingEmployees] = React.useState(false);
    const [isLoadingClients, setIsLoadingClients] = React.useState(false);
    const [isLoadingRoles, setIsLoadingRoles] = React.useState(false);

    const resolveProjectsPath = React.useCallback(() => {
        const routeTypeId = params.typeId ? Number(params.typeId) : undefined;
        const fallbackTypeId = routeTypeId;
        if (fallbackTypeId != null && !Number.isNaN(fallbackTypeId)) {
            return NAVIGATION_ROUTES.PROJECTS_BY_TYPE.replace(':typeId', String(fallbackTypeId));
        }
        return NAVIGATION_ROUTES.PROJECTS;
    }, [params.typeId]);
    const canEditProjects = canManageProjects(profile);
    const canEditClient = canManageProjectClient(profile);
    const canEditProjectManager = canManageProjectManager(profile);
    const canEditEmployees = canManageProjectEmployees(profile);
    const canEditRoleForNotification = canManageProjectRoleForNotification(profile);
    const canEditProjectType = canManageProjectType(profile);
    const selectedType = React.useMemo(() => {
        const routeTypeId = params.typeId ? Number(params.typeId) : undefined;
        if (routeTypeId != null && !Number.isNaN(routeTypeId)) {
            return types.find((type) => type.id === routeTypeId) ?? null;
        }

        return types[0] ?? null;
    }, [params.typeId, types]);

    useEffect(() => {
        if (!canEditProjects) {
            navigate(resolveProjectsPath());
            return;
        }

        if (types.length === 0) {
            dispatch(getProjectTabs());
        }
    }, [
        canEditProjects,
        dispatch,
        navigate,
        resolveProjectsPath,
        types.length,
    ]);

    const ensureEmployeesLoaded = useCallback(() => {
        if (!(canEditProjectManager || canEditEmployees) || employees.length > 0 || isLoadingEmployees) {
            return;
        }

        setIsLoadingEmployees(true);
        dispatch(getAllEmployees())
            .finally(() => setIsLoadingEmployees(false));
    }, [canEditEmployees, canEditProjectManager, dispatch, employees.length, isLoadingEmployees]);

    const ensureClientsLoaded = useCallback(() => {
        if (!canEditClient || clients.length > 0 || isLoadingClients) {
            return;
        }

        setIsLoadingClients(true);
        dispatch(getAllClients())
            .finally(() => setIsLoadingClients(false));
    }, [canEditClient, clients.length, dispatch, isLoadingClients]);

    const ensureRolesLoaded = useCallback(() => {
        if (!canEditRoleForNotification || roles.length > 0 || isLoadingRoles) {
            return;
        }

        setIsLoadingRoles(true);
        dispatch(getRoles())
            .finally(() => setIsLoadingRoles(false));
    }, [canEditRoleForNotification, dispatch, isLoadingRoles, roles.length]);

    if (!canEditProjects || !selectedType) {
        return null;
    }

    const initialValue: CreateForm = {
        name: '',
        isActive: true,
        isBillable: false,
        projectType: selectedType,
        employees: [],
        note: '',
    };

    const validateName = async (name: string): Promise<boolean> => {
        const normalized = name.trim().toLowerCase();
        if (normalized === '') {
            return Promise.resolve(false);
        }

        return dispatch(filterProjects(normalized)).unwrap().then(values => {
            return values.length < 1 || values.some(value => value.name.toLowerCase() !== normalized);
        });
    };

    const add = async (values: CreateForm) => {
        const payload: Project = {
            id: 0,
            name: values.name,
            isActive: values.isActive,
            isBillable: values.isBillable,
            note: values.note ?? '',
            client: values.client,
            projectType: values.projectType,
            projectManager: values.projectManager,
            roleForNotification: values.roleForNotification,
            employees: values.employees ?? [],
            canEditClient: canEditProjects,
            canEditProjectManager: canEditProjects,
            canEditEmployees: canEditProjects,
            canEditRoleForNotification: canEditProjects,
        };

        await dispatch(addProject(payload)).unwrap();
        navigate(resolveProjectsPath());
    };

    const validationScheme: Yup.Schema<CreateForm> = Yup.object().shape({
        name: Yup.string()
            .max(PROJECT_NAME_MAX_LENGTH, `Must be ${PROJECT_NAME_MAX_LENGTH} characters or less`)
            .required(intl.formatMessage({ id: Lx.Form.NAME_REQUIRED_FORMAT }, { s: intl.formatMessage({ id: Lx.General.PROJECT }) }))
            .test(
                'project-name-backend-validation',
                intl.formatMessage({ id: Lx.Project.NAME_TAKEN }),
                async (name: string) => validateName(name),
            ),
        isActive: Yup.boolean().required(),
        isBillable: Yup.boolean().required(),
        projectType: Yup.object().shape({
            id: Yup.number().required(),
            name: Yup.string().required(),
            isDefault: Yup.boolean().required(),
        }).required(),
    });

    return (
        <Formik
            initialValues={initialValue}
            validationSchema={validationScheme}
            validateOnBlur
            validateOnChange={false}
            onSubmit={add}
        >
            {({ values }) => (
                <Form>
                    <BaseForm
                        contentGap={24}
                        formType='create'
                        formHeader={Lx.Project.CREATE_PROJECT}
                        elementName={values.name.trim() || undefined}
                        savingTitle={intl.formatMessage({ id: Lx.General.PROJECT })}
                        onClose={() => navigate(resolveProjectsPath())}
                        onAfterSave={() => navigate(resolveProjectsPath())}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%', maxWidth: '500px' }}>
                            <FormInput
                                name='name'
                                label={intl.formatMessage({ id: Lx.General.NAME })}
                                placeholder={intl.formatMessage({ id: Lx.General.NAME_PLACEHOLDER })}
                                required
                                limited
                                limit={PROJECT_NAME_MAX_LENGTH}
                            />
                            {canEditClient ? (
                                <DropdownField
                                    label={intl.formatMessage({ id: Lx.General.CLIENT })}
                                    name='client'
                                    required={false}
                                    placeholder=''
                                    items={clients}
                                    onFocus={ensureClientsLoaded}
                                />
                            ) : null}
                            {canEditProjectType ? (
                                <DropdownField
                                    label={intl.formatMessage({ id: Lx.General.TYPE })}
                                    name='projectType'
                                    required
                                    items={types}
                                    placeholder=''
                                />
                            ) : null}
                            {canEditProjectManager ? (
                                <DropdownField
                                    label={intl.formatMessage({ id: Lx.General.PROJECT_MANAGER })}
                                    name='projectManager'
                                    required={false}
                                    placeholder=''
                                    items={employees}
                                    onFocus={ensureEmployeesLoaded}
                                />
                            ) : null}
                            {canEditEmployees ? (
                                <FormAutocomplete
                                    placeHolder={intl.formatMessage({ id: Lx.General.START_TYPING })}
                                    label={intl.formatMessage({ id: Lx.General.EMPLOYEE })}
                                    name='employees'
                                    items={employees}
                                    onFocus={ensureEmployeesLoaded}
                                />
                            ) : null}
                            {canEditRoleForNotification ? (
                                <DropdownField
                                    label={intl.formatMessage({ id: Lx.General.ROLE_FOR_NOTIFICATION })}
                                    name='roleForNotification'
                                    required={false}
                                    placeholder=''
                                    items={roles}
                                    onFocus={ensureRolesLoaded}
                                />
                            ) : null}
                            <FormInput
                                name='note'
                                label={intl.formatMessage({ id: Lx.General.NOTE })}
                                placeholder={intl.formatMessage({ id: Lx.General.WRITE_SOMETHING })}
                                required={false}
                            />
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <FormCheckbox name='isActive' label={intl.formatMessage({ id: Lx.General.ACTIVE })} />
                                <FormCheckbox name='isBillable' label='Billable' />
                                <FormCheckbox name='isDefault' label={intl.formatMessage({ id: Lx.General.DEFAULT })} />
                            </div>
                        </div>
                    </BaseForm>
                </Form>
            )}
        </Formik>
    );
}
